import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreVertical, Archive } from "lucide-react";
import ArchiveConfirmationModal from "@/components/agent/modals/ArchiveConfirmationModal";
import { useArchiveVoiceAgent } from "@/hooks/voice/useArchiveVoiceAgent";

interface VoiceAgentContextMenuProps {
  agent: {
    id: string;
    title: string;
    agent_name?: string;
  };
  onArchiveSuccess?: () => void;
}

const VoiceAgentContextMenu = ({
  agent,
  onArchiveSuccess,
}: VoiceAgentContextMenuProps) => {
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const { archive, isArchiving } = useArchiveVoiceAgent();

  const handleArchiveClick = () => {
    setIsArchiveModalOpen(true);
  };

  const handleArchiveConfirm = async () => {
    try {
      const success = await archive(agent.id);
      if (success) {
        setIsArchiveModalOpen(false);
        onArchiveSuccess?.();
      } else {
        // Archive function already shows error toast, just keep modal open for retry
        console.log("Archive failed, modal remains open for retry");
      }
    } catch (err) {
      console.error("Unexpected error during archive:", err);
      // Archive hook already handles errors with toast, no additional action needed
    }
  };

  const agentDisplayName =
    agent.agent_name || agent.title || "Untitled Voice Agent";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg"
          >
            <MoreVertical className="h-4 w-4 text-gray-600" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem
            onClick={handleArchiveClick}
            className="text-orange-600 focus:text-orange-700 focus:bg-orange-50"
          >
            <Archive className="mr-2 h-4 w-4" />
            Archive
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ArchiveConfirmationModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        onConfirm={handleArchiveConfirm}
        agentName={agentDisplayName}
        isArchiving={isArchiving}
      />
    </>
  );
};

export default VoiceAgentContextMenu;
